import React, { useState } from 'react';

// Composant pour afficher une annonce compacte dans la liste déroulante
const CompactListingItem = ({ listing, onViewDetails }) => (
  <div 
    onClick={(e) => {
      e.stopPropagation();
      onViewDetails(listing.id); // Passer seulement l'ID au lieu de l'objet entier
    }}
    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
  >
    <img 
      src={listing.images?.[0]?.secure_url || listing.images?.[0]?.url || 'https://via.placeholder.com/50x50?text=No+Image'}
      alt={listing.title}
      className="w-12 h-12 rounded-lg object-cover"
      onError={(e) => {
        e.target.src = 'https://via.placeholder.com/50x50?text=No+Image';
      }}
    />
    <div className="flex-1 min-w-0">
      <h4 className="text-sm font-medium text-gray-900 truncate">{listing.title}</h4>
      <div className="flex items-center justify-between mt-1">
        <span className="text-sm font-semibold text-blue-600">€{listing.price}</span>
        {listing.location && (
          <span className="text-xs text-gray-500 truncate ml-2">📍 {listing.location}</span>
        )}
      </div>
    </div>
    <div className="text-gray-400">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </div>
);

// Category Card Component avec fonctionnalité déroulante conditionnelle
export const CategoryCard = ({ category, onClick, listings = [], onViewDetails, enableDropdown = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const categoryListings = listings.filter(listing => listing.category_id === category.id);
  
  const handleCardClick = () => {
    if (enableDropdown && categoryListings.length > 0) {
      setIsExpanded(!isExpanded);
    } else {
      // Mode simple : directement filtrer par catégorie
      if (onClick) {
        onClick(category);
      }
    }
  };

  const handleViewAllClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(category); // Aller à la vue complète des annonces de cette catégorie
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <div 
        onClick={handleCardClick}
        className="p-4 cursor-pointer active:scale-95 transition-transform"
      >
        <div className="text-center">
          <div className="text-3xl mb-2">{category.icon}</div>
          <h3 className="font-medium text-gray-900 text-sm">{category.name}</h3>
          {category.count !== undefined && category.count !== null && (
            <p className="text-xs text-gray-500 mt-1">{category.count} annonce{category.count > 1 ? 's' : ''}</p>
          )}
          {/* Afficher la flèche seulement si le dropdown est activé et qu'il y a des annonces */}
          {enableDropdown && categoryListings.length > 0 && (
            <div className="mt-2">
              <svg 
                className={`w-4 h-4 mx-auto text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          )}
          {/* Bouton "Voir toutes" seulement si dropdown activé et pas d'annonces */}
          {enableDropdown && categoryListings.length === 0 && (
            <div className="mt-2">
              <button 
                onClick={handleViewAllClick}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Voir toutes les annonces
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Liste déroulante des annonces - seulement si dropdown activé */}
      {enableDropdown && isExpanded && categoryListings.length > 0 && (
        <div className="border-t border-gray-100 p-3 bg-gray-25">
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {categoryListings.slice(0, 5).map(listing => (
              <CompactListingItem 
                key={listing.id} 
                listing={listing} 
                onViewDetails={onViewDetails}
              />
            ))}
            {categoryListings.length > 5 && (
              <div className="text-center pt-2">
                <button 
                  onClick={handleViewAllClick}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Voir les {categoryListings.length - 5} autres annonces...
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Categories Grid Component
export const CategoriesGrid = ({ categories, onCategoryClick, listings = [], onViewDetails, enableDropdown = false }) => (
  <div className="p-4">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Categories</h2>
    <div className="grid grid-cols-2 gap-3">
      {categories.map(category => (
        <CategoryCard 
          key={category.id} 
          category={category} 
          onClick={onCategoryClick}
          listings={listings}
          onViewDetails={onViewDetails}
          enableDropdown={enableDropdown}
        />
      ))}
    </div>
  </div>
);
